import React from 'react';
import NoItemsYetContent from '../../components/manager/no-items-yet-content';
import EmployeesLimitReachedAction from '../../components/manager/employees-limit-reached-action';
import {
    TextDefault,
    FlexDiv,
    StatusBadge,
    palette,
} from '@awarego-ui/components';
import { Link } from 'react-router-dom';

export function createNoUsersView(
    currentCompany,
    createDisabled,
    entity,
    showIntegrations = true
) {
    const imgName =
        entity == 'users' ? 'employees-empty.svg' : 'employee-list-empty.svg';
    const imgAlt =
        entity == 'users'
            ? 'empty employees view'
            : 'empty employee lists view';
    const title =
        entity == 'users' ? 'No employees yet' : 'No employee lists yet';
    return (
        <FlexDiv justifyCenter>
            <NoItemsYetContent
                img={`/static/img/${imgName}`}
                imgAlt={imgAlt}
                title={title}
                extraContent={
                    createDisabled ? (
                        <EmployeesLimitReachedAction
                            disabled={createDisabled}
                            currentCompany={currentCompany}
                            centered
                        />
                    ) : (
                        showIntegrations && (
                            <TextDefault mt={24}>
                                Or{' '}
                                <TextDefault themed bold>
                                    <Link to="/settings/integrations">
                                        set up integrations
                                    </Link>
                                </TextDefault>{' '}
                                to sync your employees automatically
                            </TextDefault>
                        )
                    )
                }
            />
        </FlexDiv>
    );
}

function parseMetadata(metadata) {
    if (!metadata) return null;
    if (typeof metadata === 'string') {
        try {
            return JSON.parse(metadata);
        } catch (err) {
            return null;
        }
    }
    return metadata;
}

export function getUserListTags(list) {
    const metadata = parseMetadata(list?.metadata);
    if (!metadata) return [];
    const tags = metadata.tags;
    if (Array.isArray(tags)) {
        return tags.map((t) => String(t).trim()).filter(Boolean);
    }
    if (typeof tags === 'string' && tags.trim()) {
        return tags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean);
    }
    return [];
}

export function createBadge(sourceOrList) {
    const metadata =
        sourceOrList && typeof sourceOrList === 'object'
            ? parseMetadata(sourceOrList.metadata)
            : null;
    const source =
        sourceOrList && typeof sourceOrList === 'object'
            ? sourceOrList.source
            : sourceOrList;

    if (metadata && metadata.type === 'awarenessImprovements') {
        return (
            <StatusBadge
                color={palette.evergreen.aspargus}
                text={'dynamic'}
            />
        );
    }

    switch (source) {
        case 'azure':
            return (
                <StatusBadge
                    color={palette.evergreen.aspargus}
                    text={'AD imported'}
                />
            );
        case 'google':
            return (
                <StatusBadge
                    color={palette.evergreen.aspargus}
                    text={'Google imported'}
                />
            );

        case 'vanta':
            return (
                <StatusBadge
                    color={palette.evergreen.aspargus}
                    text={'Vanta imported'}
                />
            );
        default:
            break;
    }
}

export function formatTagColumnHolders(row) {
    if (row && row.tagColumnHoldersLabel) return row.tagColumnHoldersLabel;
    return (row.tagColumnHolders || [])
        .map((h) => h.name || h.email)
        .filter(Boolean)
        .join(', ');
}

function settingValue(settings, key) {
    if (!settings) return '';
    if (Array.isArray(settings)) {
        const row = settings.find((s) => s && s.name === key);
        const raw = row && row.value;
        if (Array.isArray(raw)) return String(raw[0] || '').trim();
        return String(raw || '').trim();
    }
    if (typeof settings === 'object') {
        const raw = settings[key];
        if (Array.isArray(raw)) return String(raw[0] || '').trim();
        return String(raw || '').trim();
    }
    return '';
}

/** Tag name for the lists table: company setting and/or `tagColumn` on list rows. */
export function resolveTagColumnFromCompany(company, rows = []) {
    const fromSettings =
        settingValue(company && company.uiSettings, 'ui.Tag_column') ||
        settingValue(company && company.settings, 'ui.Tag_column');
    if (fromSettings) return fromSettings;
    const list = rows && typeof rows.length === 'number' ? rows : [];
    for (let i = 0; i < list.length; i++) {
        const t = String((list[i] && list[i].tagColumn) || '').trim();
        if (t) return t;
    }
    return '';
}

export function tagColumnHoldersColumn(tagColumn, extra = {}) {
    if (!tagColumn) return null;
    return {
        Header: tagColumn,
        id: 'tagColumnHolders',
        accessor: formatTagColumnHolders,
        Cell: ({ value }) => value || '—',
        ...extra,
    };
}
